// src/components/common/QrCodeInput.js

import React, { useState } from 'react';
import PasswordInput from './PasswordInput';
import QrScannerModal from './QrScannerModal';

export default function QrCodeInput({ id, name, value, onChange, placeholder, readOnly = false }) {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const handleScanSuccess = (decodedText) => {
        try {
            // Suporte para otpauth:// e segredos puros
            let secret = decodedText;
            if (decodedText.startsWith('otpauth://')) {
                const url = new URL(decodedText);
                secret = url.searchParams.get('secret');
            }
            
            if (secret) {
                const syntheticEvent = {
                    target: {
                        name: name,
                        value: secret,
                    },
                };
                onChange(syntheticEvent);
                setShowManual(false);
            } else {
                throw new Error("Secret não encontrado no QR Code");
            }
        } catch (error) {
            console.error("QR Code inválido:", error);
            alert("QR Code inválido. Certifique-se de que é um código de autenticação válido.");
        }
        setIsScannerOpen(false);
    };

    return (
        <div className="space-y-3">
            <div className="relative">
                <button
                    type="button"
                    onClick={() => !readOnly && setIsScannerOpen(true)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${!readOnly ? 'active:scale-[0.98]' : 'cursor-default'} ${
                        value 
                        ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' 
                        : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600 hover:bg-slate-800'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${value ? 'bg-cyan-500/20' : 'bg-slate-700'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <div className="flex flex-col items-start translate-y-[-1px] min-w-0 pr-4">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] leading-none mb-1.5 opacity-70">
                                {value ? 'Chave 2FA Configurada' : 'Escanear QR Code'}
                            </span>
                            <span className={`font-bold transition-all truncate w-full ${isVisible ? 'text-sm text-cyan-200 tracking-wider' : 'text-[11px] opacity-50 tracking-normal'}`}>
                                {value ? (isVisible ? value : `Secret: ${value.substring(0, 4)}••••${value.substring(value.length - 2)}`) : 'Clique para ativar a câmera'}
                            </span>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {value && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsVisible(!isVisible);
                                }}
                                className="p-2 hover:bg-cyan-400/20 rounded-lg text-cyan-400 transition-all active:scale-90"
                                title={isVisible ? "Esconder Chave" : "Mostrar Chave"}
                            >
                                {isVisible ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057-5.064-7 9.542-7 .847 0 1.67.127 2.455.364m0 12.291A9.959 9.959 0 0021.542 12c-1.274-4.057-5.064-7-9.542-7a9.953 9.953 0 00-4.293 1.175M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                )}
                            </button>
                        )}
                        {value && (
                            <div className="bg-cyan-500 text-slate-950 p-1 rounded-full shadow-lg shadow-cyan-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        )}
                    </div>
                </button>
            </div>

            {!showManual && !readOnly && (
                <button 
                    type="button" 
                    onClick={() => setShowManual(true)} 
                    className="text-[10px] font-black text-slate-500 hover:text-cyan-400 uppercase tracking-widest px-1 transition-colors flex items-center gap-2"
                >
                    <span className="w-4 h-px bg-slate-800"></span>
                    Ou digitar manualmente
                </button>
            )}

            {showManual && (
                <div className="animate-in slide-in-from-top-2 duration-200">
                    <input
                        id={id}
                        name={name}
                        type="text"
                        value={value || ''}
                        onChange={onChange}
                        placeholder="Cole a chave secreta (32 caracteres)"
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-700 text-sm"
                    />
                    <button 
                        type="button" 
                        onClick={() => setShowManual(false)} 
                        className="mt-2 text-[9px] font-bold text-slate-600 hover:text-slate-400 uppercase tracking-tighter px-1"
                    >
                        Esconder campo manual
                    </button>
                </div>
            )}

            {isScannerOpen && (
                <QrScannerModal
                    onClose={() => setIsScannerOpen(false)}
                    onScanSuccess={handleScanSuccess}
                />
            )}
        </div>
    );
}